import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { colors, gradients, spacing, breakpoints } from '../../constants/theme';
import { getCurrentUserDomainName } from '../../utils/domainUtils';
import { getUserNavigationItems } from '../../utils/roleUtils';
import logoImage from '../../assets/intuition_ai_logo.png';
import CreditsBalance from '../credits/CreditsBalance';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${colors.background.primary};
`;

const Sidebar = styled.div<{ isOpen: boolean }>`
  width: 280px;
  background: ${colors.background.secondary};
  border-right: 1px solid ${colors.background.tertiary};
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  z-index: 1000;
  transition: transform 0.3s ease;

  @media (max-width: ${breakpoints.tablet}) {
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

const SidebarHeader = styled.div`
  padding: ${spacing.lg};
  border-bottom: 1px solid ${colors.background.tertiary};
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;

const Logo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: ${colors.background.tertiary};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
    transform: rotate(45deg);
    transition: all 0.6s ease;
  }

  &:hover::before {
    animation: shimmer 1.5s ease-in-out;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) rotate(45deg); }
    100% { transform: translateX(100%) rotate(45deg); }
  }
`;

const BrandText = styled.div`
  color: ${colors.text.primary};
  font-size: 20px;
  font-weight: 600;
  background: ${gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: ${spacing.lg} 0;
`;

const NavItem = styled.div<{ active?: boolean }>`
  padding: ${spacing.md} ${spacing.lg};
  color: ${props => props.active ? colors.primary.main : colors.text.secondary};
  background: ${props => props.active ? `${colors.primary.main}15` : 'transparent'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  margin: 2px ${spacing.md};
  border-radius: 12px;
  position: relative;

  &:hover {
    background: ${props => props.active ? `${colors.primary.main}20` : `${colors.background.tertiary}40`};
    color: ${props => props.active ? colors.primary.light : colors.text.primary};
    transform: translateX(4px);
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: ${props => props.active ? '20px' : '0px'};
    background: ${colors.primary.main};
    border-radius: 0 2px 2px 0;
    transition: all 0.3s ease;
  }

  &:hover::before {
    height: 20px;
    background: ${props => props.active ? colors.primary.main : colors.primary.light};
  }
`;

const NavIcon = styled.span`
  font-size: 18px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SidebarFooter = styled.div`
  padding: ${spacing.lg};
  border-top: 1px solid ${colors.background.tertiary};
  
  @media (max-width: ${breakpoints.tablet}) {
    padding: ${spacing.md};
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.background.tertiary};
  border-radius: 8px;
  margin-bottom: ${spacing.md};
  
  @media (max-width: ${breakpoints.tablet}) {
    padding: ${spacing.sm};
    margin-bottom: ${spacing.sm};
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  background: ${gradients.secondary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.primary};
  font-weight: 600;
  font-size: 14px;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 20px ${colors.primary.main}30;
  }
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  color: ${colors.text.primary};
  font-size: 14px;
  font-weight: 500;
`;

const UserEmail = styled.div`
  color: ${colors.text.secondary};
  font-size: 12px;
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: ${spacing.md};
  background: transparent;
  border: 1px solid ${colors.background.tertiary};
  color: ${colors.text.secondary};
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.error}20;
    border-color: ${colors.error};
    color: ${colors.error};
  }
`;

const MainContent = styled.div<{ sidebarOpen: boolean }>`
  flex: 1;
  margin-left: 280px;
  background: ${colors.background.primary};
  min-height: 100vh;
  transition: margin-left 0.3s ease;

  @media (max-width: ${breakpoints.tablet}) {
    margin-left: 0;
    filter: ${props => props.sidebarOpen ? 'brightness(0.7)' : 'none'};
  }
`;

const ContentArea = styled.div`
  padding: ${spacing.lg};
  max-width: 1400px;
  margin: 0 auto;
`;

const TopHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  background: ${colors.background.primary};
  border-bottom: 1px solid ${colors.background.tertiary};
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
  
  @media (max-width: ${breakpoints.tablet}) {
    padding: ${spacing.sm} ${spacing.md};
    justify-content: space-between;
  }
`;

const HeaderLeft = styled.div`
  display: none;
  
  @media (max-width: ${breakpoints.tablet}) {
    display: flex;
    align-items: center;
    gap: ${spacing.md};
    margin-left: 44px; /* Account for mobile menu button */
  }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  color: ${colors.text.primary};
  font-size: 1.2rem;
  font-weight: 600;
`;

// Mobile components
const MobileMenuButton = styled.button`
  display: none;
  position: fixed;
  top: ${spacing.md};
  left: ${spacing.md};
  z-index: 1001;
  background: ${colors.background.secondary};
  border: 1px solid ${colors.background.tertiary};
  color: ${colors.text.primary};
  padding: ${spacing.sm};
  border-radius: 8px;
  cursor: pointer;
  font-size: 20px;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;

  @media (max-width: ${breakpoints.tablet}) {
    display: flex;
  }

  &:hover {
    background: ${colors.background.tertiary};
  }
`;

const MobileOverlay = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;

  @media (max-width: ${breakpoints.tablet}) {
    display: block;
  }
`;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getInitials = (email: string) => {
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const getDomainName = getCurrentUserDomainName;

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavItemClick = (path: string) => {
    navigate(path);
    closeSidebar(); // Close sidebar on mobile after navigation
  };

  // Only render navigation items after userData is loaded to prevent admin panel flash
  // This ensures we don't show admin navigation before role verification is complete
  const navItems = userData ? getUserNavigationItems(
    userData.role,
    userData.email,
    location.pathname
  ) : [];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/dashboard');
      closeSidebar();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleCreditsClick = () => {
    navigate('/admin?tab=credits');
    closeSidebar();
  };

  return (
    <LayoutContainer>
      <MobileMenuButton onClick={toggleSidebar}>
        {sidebarOpen ? '✕' : '☰'}
      </MobileMenuButton>

      <MobileOverlay isOpen={sidebarOpen} onClick={closeSidebar} />

      <Sidebar isOpen={sidebarOpen}>
        <SidebarHeader>
          <Logo>
            <img src={logoImage} alt="Intuition AI" />
          </Logo>
          <BrandText>Intuition AI</BrandText>
        </SidebarHeader>
        
        <SidebarNav>
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              active={item.active}
              onClick={() => handleNavItemClick(item.path)}
            >
              <NavIcon>{item.icon}</NavIcon>
              {item.label}
            </NavItem>
          ))}
        </SidebarNav>

        <SidebarFooter>
          <UserInfo>
            <UserAvatar>
              {userData?.email ? getInitials(userData.email) : 'U'}
            </UserAvatar>
            <UserDetails>
              <UserName>{getDomainName()}</UserName>
              <UserEmail>{userData?.email}</UserEmail>
            </UserDetails>
          </UserInfo>
          <LogoutButton onClick={handleLogout}>
            Sign Out
          </LogoutButton>
        </SidebarFooter>
      </Sidebar>

      <MainContent sidebarOpen={sidebarOpen}>
        <TopHeader>
          <HeaderLeft>
            <HeaderTitle>Intuition AI Portal</HeaderTitle>
          </HeaderLeft>
          <CreditsBalance onClick={handleCreditsClick} />
        </TopHeader>
        
        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout;
